import { changePassword } from '@bb/auth-core';
import { useAuth, userPoolIdFromAuthority } from '@bb/session-manager';
import { Button, Input, toast } from '@rocket/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { BBDialog, Flex } from '..';

export interface ChangePasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

// biome-ignore lint/suspicious/noExplicitAny: @bb/session-manager's useAuth is untyped JS, matching the rest of the app's usage
type AnyData = any;

// Cognito exception names we handle specially. `InvalidPasswordException` isn't listed here on
// purpose: its message already describes which pool-policy rule failed, and we don't have a
// reliable copy of the real policy to phrase our own version of that message.
const ERROR_MESSAGES: Record<string, string> = {
  NotAuthorizedException: 'Current password is incorrect.',
  LimitExceededException: 'Too many attempts. Please try again later.',
  TooManyRequestsException: 'Too many attempts. Please try again later.',
  PasswordResetRequiredException:
    'Your password must be reset before you can change it here. Please sign out and use "Forgot password" on the sign-in page.',
  MFA_REQUIRED_UNSUPPORTED:
    "Accounts with multi-factor authentication can't change their password here yet. Contact support.",
};

function describeChangePasswordError(error: unknown): string {
  const name = (error as { name?: string; code?: string })?.name || (error as AnyData)?.code;
  const message = (error as { message?: string })?.message;
  return (
    ERROR_MESSAGES[name as string] || message || 'Could not change password. Please try again.'
  );
}

const initialFields = { currentPassword: '', newPassword: '', confirmPassword: '' };

export const ChangePasswordDialog = ({ isOpen, onClose }: ChangePasswordDialogProps) => {
  const { config, user }: AnyData = useAuth();
  const [fields, setFields] = useState(initialFields);
  const [validationError, setValidationError] = useState('');

  const handleClose = () => {
    setFields(initialFields);
    setValidationError('');
    onClose();
  };

  const mutation = useMutation({
    mutationFn: () =>
      changePassword(user.email, fields.currentPassword, fields.newPassword, {
        userPoolId: userPoolIdFromAuthority(config?.oidcAuth?.authority) as string,
        clientId: config?.oidcAuth?.clientId,
      }),
    onSuccess: () => {
      toast.success('Password changed');
      handleClose();
    },
    onError: (error) => setValidationError(describeChangePasswordError(error)),
  });

  const handleSubmit = () => {
    setValidationError('');
    if (!fields.currentPassword || !fields.newPassword || !fields.confirmPassword) {
      setValidationError('Please fill in all fields.');
      return;
    }
    if (fields.newPassword.length < 8) {
      setValidationError('New password must be at least 8 characters.');
      return;
    }
    if (fields.newPassword !== fields.confirmPassword) {
      setValidationError('New passwords do not match.');
      return;
    }
    mutation.mutate();
  };

  return (
    <BBDialog
      size="sm"
      title="Change Password"
      isOpen={isOpen}
      onClose={handleClose}
      actions={
        <Flex.H sx={{ gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </Flex.H>
      }
    >
      <Flex.V gap="12px">
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={fields.currentPassword}
          onChange={(e) => setFields({ ...fields, currentPassword: e.target.value })}
          disabled={mutation.isPending}
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={fields.newPassword}
          onChange={(e) => setFields({ ...fields, newPassword: e.target.value })}
          disabled={mutation.isPending}
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={fields.confirmPassword}
          onChange={(e) => setFields({ ...fields, confirmPassword: e.target.value })}
          error={validationError}
          invalid={!!validationError}
          disabled={mutation.isPending}
        />
      </Flex.V>
    </BBDialog>
  );
};
